import { el, clear, onTeardown } from '../lib/dom.js';
import { loadSnapshot } from '../data/snapshot.js';
import { settings } from '../store/settings.js';
import { selection, stats, statsNational, clearGame, clearAllGames } from '../store/selection.js';
import { ui } from '../store/ui.js';
import { playedGames, isPlayed, unmarkPlayed } from '../store/playedGames.js';
import { dexesForVersionGroup, versionGroupsByGeneration } from '../data/pokedex-resolver.js';
import { chooseDialog } from '../lib/dialog.js';

function bar(seen, caught, total) {
  const pc = total ? (caught / total) * 100 : 0;
  const ps = total ? (seen / total) * 100 : 0;
  const track = el('div', { class: 'prog__track' });
  const seenEl = el('div', { class: 'prog__seen' });
  const caughtEl = el('div', { class: 'prog__caught' });
  seenEl.style.width = `${ps}%`;
  caughtEl.style.width = `${pc}%`;
  track.append(seenEl, caughtEl);
  return track;
}

export async function ProgressView() {
  const view = el('section', { class: 'view lookup progressview' });
  const snap = await loadSnapshot();

  const speciesIdsFor = (vgKey) => {
    const ids = new Set();
    for (const dex of dexesForVersionGroup(snap, vgKey)) {
      for (const [sid] of dex.entries) ids.add(sid);
    }
    return [...ids];
  };

  let editing = false;

  async function manageGame(vg) {
    const g = stats(speciesIdsFor(vg.key), vg.key);
    const isActive = settings.get().versionGroup === vg.key;
    const choices = [{ key: 'reset', label: 'Reset caught / seen for this game' }];
    if (!isActive) {
      choices.push({ key: 'remove', label: 'Remove from your games', class: 'button--danger' });
    }
    choices.push({ key: null, label: 'Cancel', class: 'button--ghost' });

    const pick = await chooseDialog({
      title: vg.name,
      body:
        g.caught || g.seen
          ? `${g.caught} caught · ${g.seen} seen tracked here.` +
            (isActive ? ' This is your selected game.' : '')
          : 'Nothing tracked here yet.' + (isActive ? ' This is your selected game.' : ''),
      choices,
    });
    if (pick === 'reset') {
      clearGame(vg.key);
      render();
    } else if (pick === 'remove') {
      clearGame(vg.key);
      unmarkPlayed(vg.key);
      render();
    }
  }

  function gameRow(vg, { played }) {
    const s = stats(speciesIdsFor(vg.key), vg.key);
    const manage = played && editing;
    return el(
      'button',
      {
        type: 'button',
        class: `prog__row${played ? ' prog__row--played' : ''}${manage ? ' prog__row--manage' : ''}`,
        onclick: manage
          ? () => manageGame(vg)
          : () => {
              settings.set({ versionGroup: vg.key, pokedex: null });
              location.hash = '#/';
            },
      },
      el('span', { class: 'prog__name' }, vg.name),
      bar(s.seen, s.caught, s.total),
      manage
        ? el('span', { class: 'prog__manage', 'aria-hidden': 'true' }, '⋯')
        : el('span', { class: 'prog__count' }, `${s.caught} / ${s.total}`),
    );
  }

  const list = el('div', { class: 'prog' });
  function render() {
    clear(list);

    const gens = versionGroupsByGeneration(snap);
    const anyPlayed = gens.some(({ versionGroups }) =>
      versionGroups.some((vg) => isPlayed(vg.key)),
    );
    const showAll = ui.get().progShowAll || !anyPlayed;

    // National total — caught in any game.
    const nat = statsNational(snap.species.map((s) => s.id));
    list.append(
      el(
        'div',
        { class: 'prog__row prog__row--nat' },
        el('span', { class: 'prog__name' }, 'National Dex'),
        bar(nat.seen, nat.caught, nat.total),
        el('span', { class: 'prog__count' }, `${nat.caught} / ${nat.total}`),
      ),
    );

    // Games you've played (imported a save for, or opened at least once).
    if (anyPlayed) {
      list.append(
        el(
          'div',
          { class: 'prog__genrow' },
          el('h3', { class: 'prog__gen' }, 'Your games'),
          el(
            'button',
            {
              type: 'button',
              class: 'prog__edit',
              onclick: () => {
                editing = !editing;
                render();
              },
            },
            editing ? 'Done' : 'Edit',
          ),
        ),
      );
      for (const { versionGroups } of gens) {
        for (const vg of versionGroups) {
          if (isPlayed(vg.key)) list.append(gameRow(vg, { played: true }));
        }
      }
    } else {
      editing = false;
    }

    // Everything else — behind a toggle, since a single unified caught
    // record makes every game report a total whether or not you've touched it.
    const rest = [];
    let restCount = 0;
    for (const { generation, versionGroups } of gens) {
      const unplayed = versionGroups.filter((vg) => !isPlayed(vg.key));
      if (!unplayed.length) continue;
      rest.push(el('h3', { class: 'prog__gen' }, `Gen ${generation.id} · ${generation.name}`));
      for (const vg of unplayed) {
        rest.push(gameRow(vg, { played: false }));
        restCount++;
      }
    }

    if (restCount) {
      if (anyPlayed) {
        list.append(
          el(
            'button',
            {
              type: 'button',
              class: 'prog__toggle',
              onclick: () => {
                ui.set({ progShowAll: !ui.get().progShowAll });
                render();
              },
            },
            showAll ? 'Hide other games' : `Show all other games (${restCount})`,
          ),
        );
      }
      if (showAll) list.append(el('div', { class: 'prog__rest' }, ...rest));
    }
  }
  render();

  async function resetEverything() {
    const nat = statsNational(snap.species.map((s) => s.id));
    if (!nat.seen && !nat.caught) return;
    const pick = await chooseDialog({
      title: 'Reset caught / seen',
      body: `Clear all ${nat.caught} caught and ${nat.seen} seen, across every game? Favorites and notes are kept.`,
      choices: [
        { key: 'reset', label: 'Reset everything', class: 'button--danger' },
        { key: null, label: 'Cancel', class: 'button--ghost' },
      ],
    });
    if (pick === 'reset') {
      clearAllGames();
      render();
    }
  }

  clear(view).append(
    el(
      'nav',
      { class: 'lookup__nav' },
      el('a', { class: 'link', href: '#/settings' }, '‹ Settings'),
    ),
    el(
      'header',
      { class: 'view__header' },
      el('h1', {}, 'Progress by game'),
      el('p', {}, 'What you’ve caught in each game. Tap one to switch to it.'),
    ),
    el(
      'div',
      { class: 'prog__bar' },
      el(
        'p',
        { class: 'prog__legend' },
        el('span', { class: 'prog__key prog__key--caught' }),
        ' caught  ',
        el('span', { class: 'prog__key prog__key--seen' }),
        ' seen',
      ),
      el(
        'button',
        { type: 'button', class: 'prog__resetall', onclick: resetEverything },
        'Reset caught / seen…',
      ),
    ),
    list,
  );

  const offSel = selection.subscribe(render);
  const offPlayed = playedGames.subscribe(render);
  onTeardown(view, () => {
    offSel();
    offPlayed();
  });
  return view;
}
